<template>
  <v-menu
    v-model="showMenu"
    :close-on-content-click="false"
    :offset="[calendarPopupNudgeRight, 0]"
    transition="scale-transition"
    min-width="auto"
  >
    <template v-slot:activator="{ props }">
      <v-text-field
        @blur="onBlur"
        @focus="hasFocus = true"
        :variant="isLineItem ? 'solo-filled' : undefined"
        :hide-details="isLineItem ? 'auto' : ''"
        ref="textField"
        v-bind:class="{ 'pt-4': !isLineItem }"
        v-maska="dateMask"
        placeholder="MM/dd/yyyy"
        :disabled="documentMetadata.metadata.isReadonly"
        v-model="formattedDate"
        :label="label"
        :append-icon="highlight && documentMetadata.autoExtracted ? 'mdi-auto-fix' : 'mdi-calendar'"
        v-bind="props"
        :bg-color="highlight ? 'metadataHighlight' : 'transparent'"
        :data-cy="documentMetadata.metadata.key"
      ></v-text-field>
    </template>
    <v-date-picker v-model="localValue" @update:modelValue="showMenu = false"></v-date-picker>
  </v-menu>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import dateService from "@/services/dateService";
import documentMetadataService from "@/services/documentMetadataService";
import responsiveService from "@/services/responsiveService";
import metadataMixin from "@/mixins/metadataMixin";

export default {
  name: "DateMetadata",

  // Use the mixin to handle prop mutations properly
  mixins: [metadataMixin],

  props: {
    // documentMetadata is provided by the mixin
    textSelection: Object,
    isLineItem: Boolean,
    focusOnLoad: Boolean,
    onClickToFill: Function,
  },
  data() {
    return {
      showMenu: false,
      highlight: false,
      manualDateEntry: null,
      hasFocus: false,
    };
  },
  watch: {
    textSelection: {
      handler() {
        // Check if this field has focus using our tracked state
        if (!this.hasFocus) return;

        const formattedDate = moment(this.textSelection.text).format("YYYY-MM-DD");
        this.localValue = formattedDate;

        documentMetadataService.assignTextSelection(this);
        this.manualDateEntry = formattedDate; // Use the formatted value directly, not from localValue getter

        // Show highlight effect when text is selected from PDF
        this.highlight = true;
        setTimeout(() => {
          this.highlight = false;
        }, 2000);

        if (this.onClickToFill) this.onClickToFill();
      },
      deep: true,
    },

    focusOnLoad() {
      documentMetadataService.focusIfRequested(this);
    },
  },

  created() {
    // Initialize localValue only if needed - normalize to remove time part
    const normalizedValue = dateService.removeTimePart(this.documentMetadata.value);
    if (normalizedValue !== this.documentMetadata.value) {
      this.localValue = normalizedValue;
    }

    // Use the mixin's helper method instead of direct mutation
    this.clearHighlightAfterDelay();
    documentMetadataService.focusIfRequested(this);
  },

  methods: {
    onBlur() {
      this.hasFocus = false;
      this.localValue = this.manualDateEntry;
    },
  },

  computed: {
    ...mapState("userProfile", ["dateFormat"]),

    label() {
      return this.isLineItem ? "" : this.documentMetadata.metadata.name;
    },

    dateMask() {
      return dateService.toMask(this.dateFormat);
    },

    calendarPopupNudgeRight() {
      return responsiveService.isMobile(this.$vuetify.display) ? -70 : 0;
    },

    // localValue is provided by the mixin and emits events on set

    formattedDate: {
      get: function() {
        if (!this.localValue || !this.dateFormat) return "";

        return moment(this.localValue).format(this.dateFormat.toUpperCase());
      },
      set: function(newValue) {
        let date = moment(newValue, this.dateFormat.toUpperCase(), true);

        if (date.isValid()) {
          this.manualDateEntry = date.format("YYYY-MM-DD");
        } else {
          this.manualDateEntry = null;
        }
      },
    },
  },
};
</script>
